
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Request} from '../core/Request';
import {FieldModelRequest} from '../core/Request';
import {BillingStatisticsGetResponse} from './BillingStatisticsGetResponse';
import {Date} from '../core/Date';
import {FieldModelDate} from '../core/Date';

/**
 * BillingStatisticsGetRequest struct
 */
class BillingStatisticsGetRequest extends Request {
  /**
   * Initialize struct
   * @param {!Request=} parent
   * @param {!Date=} from
   * @param {!Date=} to
   * @constructor
   */
  constructor (parent = new Request(), from = new Date(), to = new Date()) {
    super()
    super.copy(parent)
    this.From = from
    this.To = to
  }

  /**
   * Copy struct (shallow copy)
   * @this {!BillingStatisticsGetRequest}
   * @param {!BillingStatisticsGetRequest} other Other struct
   * @returns {!BillingStatisticsGetRequest} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.From != null) {
      this.From = Date.fromObject(other.From)
    } else {
      this.From = null
    }
    if (other.To != null) {
      this.To = Date.fromObject(other.To)
    } else {
      this.To = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!BillingStatisticsGetRequest}
   * @returns {!BillingStatisticsGetRequest} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new BillingStatisticsGetRequestModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new BillingStatisticsGetRequestModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!BillingStatisticsGetRequest}
   * @param {!BillingStatisticsGetRequest} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof BillingStatisticsGetRequest)) {
      throw new TypeError('Instance of BillingStatisticsGetRequest is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!BillingStatisticsGetRequest}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      From: ((this.From != null) ? this.From : null),
      To: ((this.To != null) ? this.To : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return BillingStatisticsGetRequest.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!BillingStatisticsGetRequest} other Object value
   * @returns {!BillingStatisticsGetRequest} Created struct
   */
  static fromObject (other) {
    return new BillingStatisticsGetRequest().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!BillingStatisticsGetRequest}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return BillingStatisticsGetRequest.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!BillingStatisticsGetRequest}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6579
  }
}

export { BillingStatisticsGetRequest };

/**
 * Fast Binary Encoding BillingStatisticsGetRequest field model
 */
class FieldModelBillingStatisticsGetRequest extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelRequest(buffer, 4 + 4)
    this.From = new FieldModelDate(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.To = new FieldModelDate(buffer, this.From.fbeOffset + this.From.fbeSize)
  }

  /**
   * Get the core.Request field model
   * @this {!FieldModelBillingStatisticsGetRequest}
   * @returns {!FieldModelRequest} core.Request field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelBillingStatisticsGetRequest}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelBillingStatisticsGetRequest}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.From.fbeSize + this.To.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelBillingStatisticsGetRequest}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.From.fbeExtra + this.To.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelBillingStatisticsGetRequest}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelBillingStatisticsGetRequest.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelBillingStatisticsGetRequest}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6579
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelBillingStatisticsGetRequest}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelBillingStatisticsGetRequest}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.From.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.From.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.From.fbeSize

    if ((fbeCurrentSize + this.To.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.To.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.To.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelBillingStatisticsGetRequest}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelBillingStatisticsGetRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelBillingStatisticsGetRequest}
   * @param {!BillingStatisticsGetRequest} fbeValue Default value, defaults is new BillingStatisticsGetRequest()
   * @returns {!BillingStatisticsGetRequest} BillingStatisticsGetRequest value
   */
  get (fbeValue = new BillingStatisticsGetRequest()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelBillingStatisticsGetRequest}
   * @param {!BillingStatisticsGetRequest} fbeValue BillingStatisticsGetRequest value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.From.fbeSize) <= fbeStructSize) {
      fbeValue.From = this.From.get()
    } else {
      fbeValue.From = new Date()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.From.fbeSize

    if ((fbeCurrentSize + this.To.fbeSize) <= fbeStructSize) {
      fbeValue.To = this.To.get()
    } else {
      fbeValue.To = new Date()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.To.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelBillingStatisticsGetRequest}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelBillingStatisticsGetRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelBillingStatisticsGetRequest}
   * @param {!BillingStatisticsGetRequest} fbeValue BillingStatisticsGetRequest value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelBillingStatisticsGetRequest}
   * @param {BillingStatisticsGetRequest} fbeValue BillingStatisticsGetRequest value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.From.set(fbeValue.From)
    this.To.set(fbeValue.To)
  }
}

export { FieldModelBillingStatisticsGetRequest };

/**
 * Fast Binary Encoding BillingStatisticsGetRequest model
 */
class BillingStatisticsGetRequestModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelBillingStatisticsGetRequest(this.buffer, 4)
  }

  /**
   * Get the BillingStatisticsGetRequest model
   * @this {!BillingStatisticsGetRequestModel}
   * @returns {!FieldModelBillingStatisticsGetRequest} model BillingStatisticsGetRequest model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!BillingStatisticsGetRequestModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!BillingStatisticsGetRequestModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return BillingStatisticsGetRequestModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!BillingStatisticsGetRequestModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelBillingStatisticsGetRequest.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!BillingStatisticsGetRequestModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!BillingStatisticsGetRequestModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!BillingStatisticsGetRequestModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!BillingStatisticsGetRequestModel}
   * @param {!BillingStatisticsGetRequest} value BillingStatisticsGetRequest value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!BillingStatisticsGetRequestModel}
   * @param {!BillingStatisticsGetRequest} value BillingStatisticsGetRequest value, defaults is new BillingStatisticsGetRequest()
   * @return {!object} Deserialized BillingStatisticsGetRequest value and its size
   */
  deserialize (value = new BillingStatisticsGetRequest()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new BillingStatisticsGetRequest(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new BillingStatisticsGetRequest(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!BillingStatisticsGetRequestModel}
   * @param {!number} prev Previous BillingStatisticsGetRequest model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { BillingStatisticsGetRequestModel };
BillingStatisticsGetRequest.__has_response = true;
BillingStatisticsGetRequest.__response_class = BillingStatisticsGetResponse;
